import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import sqfTrainingService from '../../services/sqfTrainingService';
import { useLanguage, t } from '../../context/LanguageContext';
import SqfNav from '../../components/SqfNav';

const CATEGORY_LABELS = {
  food_safety:  { en: 'Food Safety',   es: 'Inocuidad Alimentaria' },
  gmp:          { en: 'GMP',           es: 'BPM'                   },
  allergen:     { en: 'Allergen',      es: 'Alérgenos'             },
  sanitation:   { en: 'Sanitation',    es: 'Saneamiento'           },
  pest_control: { en: 'Pest Control',  es: 'Control de Plagas'     },
  traceability: { en: 'Traceability',  es: 'Trazabilidad'          },
  emergency:    { en: 'Emergency',     es: 'Emergencias'           },
  other:        { en: 'Other',         es: 'Otro'                  },
};

const CATEGORY_COLORS = {
  food_safety:  'danger',
  gmp:          'primary',
  allergen:     'warning',
  sanitation:   'info',
  pest_control: 'success',
  traceability: 'secondary',
  emergency:    'dark',
  other:        'light',
};

function StatBadge({ label, value, variant }) {
  return (
    <span className={`badge bg-${variant} bg-opacity-10 text-${variant} border border-${variant} border-opacity-25 me-1`} style={{ fontSize: 11 }}>
      {label}: {value}
    </span>
  );
}

export default function SqfTrainingLibraryPage() {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  // Confirmation modal
  const [confirmModal, setConfirmModal] = useState(null);

  // Preview modal
  const [previewTraining, setPreviewTraining] = useState(null); // { title, version, questions }
  const [previewLoading, setPreviewLoading] = useState(false);

  const handlePreview = async (tr) => {
    setPreviewLoading(true);
    try {
      const full = await sqfTrainingService.getTraining(tr.id);
      const published = (full.Versions || []).find(v => v.status === 'published');
      setPreviewTraining({
        id: full.id,
        title: full.title,
        version: published,
        questions: published?.questionsJson || [],
        material: published?.materialJson || [],
      });
    } catch {
      setError(t('Failed to load training preview.', 'Error al cargar la vista previa.', lang));
    } finally {
      setPreviewLoading(false);
    }
  };

  // New training modal
  const [showModal, setShowModal] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newCategory, setNewCategory] = useState('gmp');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    sqfTrainingService.getTrainings()
      .then(setTrainings)
      .catch(() => setError(t('Failed to load trainings.', 'Error al cargar las capacitaciones.', lang)))
      .finally(() => setLoading(false));
  }, [lang]);

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      const training = await sqfTrainingService.createTraining({
        title: newTitle.trim(),
        category: newCategory,
        description: newDesc || null,
      });
      setShowModal(false);
      setNewTitle(''); setNewCategory('gmp'); setNewDesc('');
      navigate(`/sqf/training/${training.id}/edit`);
    } catch {
      setError(t('Failed to create training.', 'Error al crear la capacitación.', lang));
    } finally {
      setCreating(false);
    }
  };

  const handleArchive = (training) => {
    const neverPublished = training.activeVersionId === null;
    setConfirmModal({
      title: neverPublished ? t('Delete Training', 'Eliminar Capacitación', lang) : t('Archive Training', 'Archivar Capacitación', lang),
      message: neverPublished
        ? t(
            `"${training.title}" has never been published — this will permanently delete it and its draft content. This cannot be undone.`,
            `"${training.title}" nunca se ha publicado — esto la eliminará permanentemente junto con su contenido borrador. Esta acción no se puede deshacer.`,
            lang
          )
        : t(
            `"${training.title}" will no longer appear in the library and cannot be assigned. Existing assignments are not affected.`,
            `"${training.title}" dejará de aparecer en la biblioteca y no podrá asignarse. Las asignaciones existentes no se ven afectadas.`,
            lang
          ),
      confirmLabel: neverPublished ? t('Delete', 'Eliminar', lang) : t('Archive', 'Archivar', lang),
      variant: 'danger',
      onConfirm: async () => {
        try {
          await sqfTrainingService.archiveTraining(training.id);
          setTrainings(ts => ts.filter(tr => tr.id !== training.id));
        } catch {
          setError(neverPublished
            ? t('Failed to delete training.', 'Error al eliminar la capacitación.', lang)
            : t('Failed to archive training.', 'Error al archivar la capacitación.', lang));
        }
      },
    });
  };

  const filtered = trainings.filter(tr => !filterCategory || tr.category === filterCategory);

  return (
    <>
      <SqfNav />
      <div className="container mt-4 pb-5">
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div>
            <Link to="/sqf" className="text-muted" style={{ fontSize: 12 }}>← {t('Dashboard', 'Panel', lang)}</Link>
            <h4 className="mb-0 mt-1">{t('Training Library', 'Biblioteca de Capacitaciones', lang)}</h4>
          </div>
          <div className="d-flex gap-2">
            <Link to="/sqf/training/assignments" className="btn btn-outline-secondary btn-sm">
              {t('Assignments', 'Asignaciones', lang)}
            </Link>
            <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>
              + {t('New Training', 'Nueva Capacitación', lang)}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger py-2">
            {error}
            <button className="btn-close float-end btn-sm" onClick={() => setError('')} />
          </div>
        )}

        {/* Category filter pills */}
        <div className="d-flex gap-2 mb-3 flex-wrap">
          {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
            <button
              key={k}
              className={`btn btn-sm ${filterCategory === k
                ? `btn-${CATEGORY_COLORS[k] || 'secondary'}`
                : 'btn-outline-secondary'}`}
              onClick={() => setFilterCategory(filterCategory === k ? '' : k)}
            >
              {t(v.en, v.es, lang)}
            </button>
          ))}
          {filterCategory && (
            <button className="btn btn-sm btn-link text-muted p-0 ms-1" onClick={() => setFilterCategory('')}>
              {t('Clear', 'Limpiar', lang)}
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-5 text-muted">
            <div className="spinner-border spinner-border-sm me-2" />{t('Loading…', 'Cargando…', lang)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <div style={{ fontSize: 40 }}>📚</div>
            <p className="mt-2">
              {filterCategory
                ? t('No trainings in this category.', 'No hay capacitaciones en esta categoría.', lang)
                : t('No trainings yet.', 'Aún no hay capacitaciones.', lang)}
            </p>
            {!filterCategory && (
              <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>
                + {t('New Training', 'Nueva Capacitación', lang)}
              </button>
            )}
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm table-bordered table-hover">
              <thead className="table-light">
                <tr>
                  <th>{t('Title', 'Título', lang)}</th>
                  <th style={{ width: 140 }}>{t('Category', 'Categoría', lang)}</th>
                  <th style={{ width: 90 }}>{t('Version', 'Versión', lang)}</th>
                  <th style={{ width: 110 }}>{t('Status', 'Estado', lang)}</th>
                  <th style={{ width: 230 }}>{t('Assignments', 'Asignaciones', lang)}</th>
                  <th style={{ width: 200 }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(tr => {
                  const activeVersion = (tr.Versions || []).find(v => v.status === 'published');
                  const draftVersion  = (tr.Versions || []).find(v => v.status === 'draft');
                  const stats = tr.stats || {};
                  const catColor = CATEGORY_COLORS[tr.category] || 'light';
                  const catLabel = CATEGORY_LABELS[tr.category];
                  return (
                    <tr key={tr.id}>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <span className="fw-semibold">{tr.title}</span>
                          {tr.languages?.includes('es') && (
                            <span
                              className="badge"
                              style={{ background: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9', fontSize: 10, fontWeight: 600 }}
                              title={t('Available in Spanish', 'Disponible en Español', lang)}
                            >
                              🌐 EN+ES
                            </span>
                          )}
                        </div>
                        {tr.description && (
                          <div className="text-muted" style={{ fontSize: 12 }}>{tr.description}</div>
                        )}
                      </td>
                      <td>
                        <span
                          className={`badge bg-${catColor} ${catColor === 'light' ? 'text-dark border' : ''}`}
                          style={{ fontSize: 11 }}
                        >
                          {catLabel ? t(catLabel.en, catLabel.es, lang) : tr.category}
                        </span>
                      </td>
                      <td style={{ fontSize: 12 }}>
                        {activeVersion ? `v${activeVersion.versionNumber}` : '—'}
                        {draftVersion && (
                          <span className="badge bg-secondary ms-1" style={{ fontSize: 9 }}>
                            {t('draft', 'borrador', lang)}
                          </span>
                        )}
                        {activeVersion && (
                          <div className="text-muted" style={{ fontSize: 10 }}>
                            {t('Pass', 'Aprobación', lang)}: {activeVersion.passingScore ?? 80}%
                          </div>
                        )}
                      </td>
                      <td>
                        {tr.activeVersionId
                          ? <span className="badge bg-success">{t('Published', 'Publicado', lang)}</span>
                          : <span className="badge bg-secondary">{t('No version', 'Sin versión', lang)}</span>}
                      </td>
                      <td style={{ fontSize: 12 }}>
                        {stats.total > 0 ? (
                          <>
                            <StatBadge label={t('Total', 'Total', lang)} value={stats.total} variant="secondary" />
                            {stats.passed > 0 && <StatBadge label={t('Passed', 'Aprobados', lang)} value={stats.passed} variant="success" />}
                            {stats.pending > 0 && <StatBadge label={t('Pending', 'Pendientes', lang)} value={stats.pending} variant="warning" />}
                            {stats.failed > 0 && <StatBadge label={t('Failed', 'Fallidos', lang)} value={stats.failed} variant="danger" />}
                          </>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                        {tr.activeVersionId && (
                          <button
                            className="btn btn-outline-info btn-sm me-1"
                            onClick={() => handlePreview(tr)}
                            disabled={previewLoading}
                            title={t('View published questions', 'Ver preguntas publicadas', lang)}
                          >
                            {t('Preview', 'Ver', lang)}
                          </button>
                        )}
                        <Link to={`/sqf/training/${tr.id}/edit`} className="btn btn-outline-primary btn-sm me-1">
                          {t('Edit', 'Editar', lang)}
                        </Link>
                        <Link to={`/sqf/training/assignments?trainingId=${tr.id}`} className="btn btn-outline-secondary btn-sm me-1">
                          {t('Assign', 'Asignar', lang)}
                        </Link>
                        <button className="btn btn-outline-danger btn-sm" onClick={() => handleArchive(tr)}>
                          {tr.activeVersionId === null ? t('Delete', 'Eliminar', lang) : t('Archive', 'Archivar', lang)}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* New Training Modal */}
        {showModal && (
          <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.4)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{t('New Training', 'Nueva Capacitación', lang)}</h5>
                  <button className="btn-close" onClick={() => setShowModal(false)} />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      {t('Title', 'Título', lang)} <span className="text-danger">*</span>
                    </label>
                    <input
                      className="form-control"
                      value={newTitle}
                      onChange={e => setNewTitle(e.target.value)}
                      placeholder={t('e.g. GMP Refresher Training', 'ej. Capacitación de Repaso de BPM', lang)}
                      autoFocus
                      onKeyDown={e => e.key === 'Enter' && handleCreate()}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">{t('Category', 'Categoría', lang)}</label>
                    <select
                      className="form-select"
                      value={newCategory}
                      onChange={e => setNewCategory(e.target.value)}
                    >
                      {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>{t(v.en, v.es, lang)}</option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">{t('Description', 'Descripción', lang)}</label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={newDesc}
                      onChange={e => setNewDesc(e.target.value)}
                      placeholder={t('Optional description', 'Descripción opcional', lang)}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    {t('Cancel', 'Cancelar', lang)}
                  </button>
                  <button className="btn btn-primary" onClick={handleCreate} disabled={creating || !newTitle.trim()}>
                    {creating
                      ? t('Creating…', 'Creando…', lang)
                      : t('Create & Edit', 'Crear y Editar', lang)}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Preview Modal */}
        {previewTraining && (
          <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1060 }} onClick={() => setPreviewTraining(null)}>
            <div className="modal-dialog modal-lg modal-dialog-scrollable" onClick={e => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header">
                  <div>
                    <h5 className="modal-title mb-0">{previewTraining.title}</h5>
                    {previewTraining.version && (
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        v{previewTraining.version.versionNumber} · {t('Published', 'Publicado', lang)}
                        {previewTraining.version.publishedAt && ` · ${new Date(previewTraining.version.publishedAt).toLocaleDateString()}`}
                        {' · '}{t('Pass', 'Aprobación', lang)}: {previewTraining.version.passingScore ?? 80}%
                        {' · '}{previewTraining.questions.length} {t('questions', 'preguntas', lang)}
                        {previewTraining.material.length > 0 && ` · ${previewTraining.material.length} ${t('material slides', 'diapositivas de material', lang)}`}
                      </div>
                    )}
                  </div>
                  <button className="btn-close" onClick={() => setPreviewTraining(null)} />
                </div>
                <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                  {previewTraining.material.length > 0 && (
                    <div className="mb-4">
                      <div className="fw-semibold mb-2" style={{ fontSize: 13 }}>
                        📖 {t('Training Material', 'Material de Capacitación', lang)}
                        <span className="text-muted fw-normal ms-2" style={{ fontSize: 11 }}>
                          {t('— what the employee reads before the quiz', '— lo que el empleado lee antes del examen', lang)}
                        </span>
                      </div>
                      {previewTraining.material.map((s, si) => (
                        <div key={s.id || si} className="mb-2 p-3 rounded" style={{ background: '#fff', border: '1px solid #dee2e6' }}>
                          <div className="d-flex align-items-start gap-2">
                            <span className="badge bg-info-subtle text-info border border-info-subtle" style={{ flexShrink: 0, marginTop: 2 }}>{si + 1}</span>
                            <div>
                              <div className="fw-semibold" style={{ fontSize: 14 }}>{s.title}</div>
                              <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{s.body}</div>
                              {(s.titleEs || s.bodyEs) && (
                                <div className="text-muted mt-2 pt-2" style={{ fontSize: 12, borderTop: '1px dashed #dee2e6' }}>
                                  🇪🇸 {s.titleEs && <strong>{s.titleEs}</strong>}
                                  {s.titleEs && s.bodyEs && <br />}
                                  {s.bodyEs}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      <div className="fw-semibold mt-4 mb-2" style={{ fontSize: 13 }}>
                        📋 {t('Quiz Questions', 'Preguntas del Examen', lang)}
                      </div>
                    </div>
                  )}
                  {previewTraining.questions.length === 0 ? (
                    <p className="text-muted">{t('No questions in this version.', 'No hay preguntas en esta versión.', lang)}</p>
                  ) : (
                    previewTraining.questions.map((q, qi) => (
                      <div key={q.id || qi} className="mb-4 p-3 rounded" style={{ background: '#f8f9fa', border: '1px solid #dee2e6' }}>
                        <div className="d-flex align-items-start gap-2 mb-2">
                          <span className="badge bg-secondary" style={{ flexShrink: 0, marginTop: 2 }}>Q{qi + 1}</span>
                          <div>
                            <div className="fw-semibold" style={{ fontSize: 14 }}>{q.text}</div>
                            {q.textEs && (
                              <div className="text-muted" style={{ fontSize: 12 }}>🇪🇸 {q.textEs}</div>
                            )}
                          </div>
                          <div className="ms-auto d-flex gap-1 flex-shrink-0">
                            {q.points != null && (
                              <span className="badge bg-light text-dark border" style={{ fontSize: 10 }}>{q.points} pts</span>
                            )}
                            <span className="badge bg-light text-dark border" style={{ fontSize: 10, textTransform: 'capitalize' }}>
                              {q.type?.replace('_', ' ') || 'single'}
                            </span>
                          </div>
                        </div>
                        <div className="d-flex flex-column gap-1 ms-4">
                          {(q.options || []).map((opt, oi) => {
                            const isCorrect = (q.correctIds || []).includes(opt.id);
                            return (
                              <div
                                key={opt.id || oi}
                                className="d-flex align-items-start gap-2 px-2 py-1 rounded"
                                style={{
                                  background: isCorrect ? '#d1e7dd' : '#fff',
                                  border: `1px solid ${isCorrect ? '#a3cfbb' : '#dee2e6'}`,
                                  fontSize: 13,
                                }}
                              >
                                <span style={{ flexShrink: 0, color: isCorrect ? '#0f5132' : '#6c757d', fontWeight: isCorrect ? 700 : 400 }}>
                                  {isCorrect ? '✓' : '○'}
                                </span>
                                <div>
                                  <span style={{ color: isCorrect ? '#0f5132' : undefined, fontWeight: isCorrect ? 600 : 400 }}>
                                    {opt.text}
                                  </span>
                                  {opt.textEs && (
                                    <span className="text-muted ms-1" style={{ fontSize: 11 }}>/ {opt.textEs}</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="modal-footer">
                  <Link to={`/sqf/training/${previewTraining.id}/edit`} className="btn btn-outline-primary btn-sm">
                    {t('Edit Training', 'Editar Capacitación', lang)}
                  </Link>
                  <button className="btn btn-secondary btn-sm" onClick={() => setPreviewTraining(null)}>
                    {t('Close', 'Cerrar', lang)}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        {confirmModal && (
          <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
              <div className="modal-content shadow">
                <div className="modal-header border-bottom-0 pb-0">
                  <h5 className="modal-title fw-semibold">{confirmModal.title}</h5>
                  <button className="btn-close" onClick={() => setConfirmModal(null)} />
                </div>
                <div className="modal-body pt-2">
                  <p className="mb-0 text-muted" style={{ fontSize: 14 }}>{confirmModal.message}</p>
                </div>
                <div className="modal-footer border-top-0 pt-0">
                  <button className="btn btn-outline-secondary" onClick={() => setConfirmModal(null)}>
                    {t('Cancel', 'Cancelar', lang)}
                  </button>
                  <button
                    className={`btn btn-${confirmModal.variant || 'primary'}`}
                    onClick={async () => {
                      const fn = confirmModal.onConfirm;
                      setConfirmModal(null);
                      await fn?.();
                    }}
                  >
                    {confirmModal.confirmLabel}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
